import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  gapErrorCode,
  type GapAcceptance,
  type GapErrorCode,
  type GapImpact,
  type GapItem,
  type GapOrigin,
  type GapWorkaroundType,
  type GapWrite,
} from "@/api/project-gaps";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useSaveGap } from "@/hooks/useProjectGaps";

import {
  ACCEPTANCE_LABELS,
  IMPACT_LABELS,
  ORIGIN_LABELS,
  WORKAROUND_TYPE_LABELS,
} from "./gapLabels";

/** O que a tela que chama já sabe do GAP: a decisão do WRICEF no Architect. */
export interface GapPrefill {
  origin_type?: GapOrigin;
  reference?: string;
  need?: string;
}

interface Draft {
  origin_type: GapOrigin | "";
  reference: string;
  need: string;
  workaround_type: GapWorkaroundType | "";
  workaround: string;
  impact: GapImpact | "";
  essential: boolean;
  owner: string;
  acceptance: GapAcceptance;
}

type RequiredKey = "origin_type" | "reference" | "need" | "workaround_type" | "impact" | "workaround";

/** Os obrigatórios da EF, na ordem do formulário, com o rótulo de cada um. */
const REQUIRED: Array<[RequiredKey, string]> = [
  ["origin_type", "Origin"],
  ["reference", "Reference"],
  ["need", "Unmet need"],
  ["workaround_type", "Workaround type"],
  ["impact", "Business impact"],
  ["workaround", "Workaround"],
];

const REFUSAL_MESSAGES: Record<GapErrorCode, string> = {
  required_field: "Fill in all required fields.",
  wricef_object_required: "Fill in all required fields.",
  wricef_object_only: "Could not save the GAP. Try again.",
  object_not_in_catalog: "The WRICEF object is not in this project's catalog.",
  not_a_deliverable:
    "This object has no WRICEF letter: it is not a deliverable and does not count in the adherence index.",
  gap_not_found: "This GAP no longer exists. Reload the list.",
  forbidden: "Only consultants can record GAPs.",
  failed: "Could not save the GAP. Try again.",
};

const SELECT_CLASS =
  "flex h-10 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-sm";

function initialDraft(gap: GapItem | undefined, prefill: GapPrefill | undefined): Draft {
  if (gap) {
    return {
      origin_type: gap.origin_type,
      reference: gap.reference,
      need: gap.need,
      workaround_type: gap.workaround_type,
      workaround: gap.workaround,
      impact: gap.impact,
      essential: gap.essential,
      owner: gap.owner ?? "",
      acceptance: gap.acceptance,
    };
  }
  return {
    origin_type: prefill?.origin_type ?? "",
    reference: prefill?.reference ?? "",
    need: prefill?.need ?? "",
    workaround_type: "",
    workaround: "",
    impact: "",
    essential: false,
    owner: "",
    acceptance: "pending",
  };
}

/**
 * O GAP que vai ao servidor. Na origem WRICEF a referência é o próprio objeto,
 * como na MOC: é por ele que o índice e a decisão do arquiteto acham o GAP.
 */
function toWrite(draft: Draft): GapWrite {
  const reference = draft.reference.trim();
  return {
    origin_type: draft.origin_type || null,
    reference,
    wricef_obj_name: draft.origin_type === "wricef" ? reference : null,
    need: draft.need.trim(),
    workaround_type: draft.workaround_type || null,
    workaround: draft.workaround.trim(),
    impact: draft.impact || null,
    essential: draft.essential,
    owner: draft.owner.trim() || null,
    acceptance: draft.acceptance,
  };
}

/**
 * Cadastro e edição de GAP (EF-FUNC-011, RF-03), o mesmo formulário nas duas
 * telas que registram GAP: a aba "Registro de GAPs" do nó 04, onde entra também
 * o do plano de contas (RF-04), e a decisão do WRICEF no Architect (RF-02), que
 * abre com `prefill`. Com `gap`, o formulário edita aquele GAP.
 *
 * Só o responsável no cliente é opcional; essencial começa em "não" e o aceite
 * em "Pendente". Fecha sozinho depois de gravar.
 */
export function GapFormDialog({
  open,
  onOpenChange,
  ...form
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  gap?: GapItem;
  prefill?: GapPrefill;
  onSaved?: (gap: GapItem) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        {open && <GapForm {...form} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function GapForm({
  projectId,
  gap,
  prefill,
  onSaved,
  onClose,
}: {
  projectId: string;
  gap?: GapItem;
  prefill?: GapPrefill;
  onSaved?: (gap: GapItem) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(() => initialDraft(gap, prefill));
  const [error, setError] = useState<string | null>(null);
  const save = useSaveGap(projectId);

  const set = (patch: Partial<Draft>) => setDraft((current) => ({ ...current, ...patch }));

  async function handleSave() {
    const missing = REQUIRED.filter(([key]) => !String(draft[key]).trim()).map(([, label]) =>
      t(label),
    );
    if (missing.length > 0) {
      setError(t("Fill in the required fields: {{fields}}.", { fields: missing.join(", ") }));
      return;
    }
    setError(null);
    try {
      const saved = await save.mutateAsync({ gapId: gap?.id, body: toWrite(draft) });
      onSaved?.(saved);
      onClose();
    } catch (err) {
      setError(t(REFUSAL_MESSAGES[gapErrorCode(err)]));
    }
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>{gap ? t("Edit {{code}}", { code: gap.code }) : t("New GAP")}</DialogTitle>
        <DialogDescription>
          {t("The client owner is optional; every other field is required.")}
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="gap-origin">{t("Origin")}</Label>
          <select
            id="gap-origin"
            value={draft.origin_type}
            onChange={(e) => set({ origin_type: e.target.value as Draft["origin_type"] })}
            className={SELECT_CLASS}
          >
            <option value="">{t("Select…")}</option>
            {Object.entries(ORIGIN_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="gap-reference">{t("Reference")}</Label>
          <Input
            id="gap-reference"
            value={draft.reference}
            maxLength={draft.origin_type === "wricef" ? 64 : 500}
            placeholder={t("WRICEF object, account(s) or process")}
            onChange={(e) => set({ reference: e.target.value })}
          />
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="gap-need">{t("Unmet need")}</Label>
          <Input
            id="gap-need"
            value={draft.need}
            maxLength={2000}
            onChange={(e) => set({ need: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="gap-workaround-type">{t("Workaround type")}</Label>
          <select
            id="gap-workaround-type"
            value={draft.workaround_type}
            onChange={(e) => set({ workaround_type: e.target.value as Draft["workaround_type"] })}
            className={SELECT_CLASS}
          >
            <option value="">{t("Select…")}</option>
            {Object.entries(WORKAROUND_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="gap-impact">{t("Business impact")}</Label>
          <select
            id="gap-impact"
            value={draft.impact}
            onChange={(e) => set({ impact: e.target.value as Draft["impact"] })}
            className={SELECT_CLASS}
          >
            <option value="">{t("Select…")}</option>
            {Object.entries(IMPACT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="gap-workaround">{t("Workaround")}</Label>
          <Textarea
            id="gap-workaround"
            rows={3}
            value={draft.workaround}
            maxLength={2000}
            placeholder={t("How the client will operate in the Public edition")}
            onChange={(e) => set({ workaround: e.target.value })}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 accent-emerald-600"
            checked={draft.essential}
            onChange={(e) => set({ essential: e.target.checked })}
          />
          {t("Essential need (blocker)")}
        </label>
        <div className="space-y-1">
          <Label htmlFor="gap-owner">{t("Client owner (optional)")}</Label>
          <Input
            id="gap-owner"
            value={draft.owner}
            maxLength={500}
            onChange={(e) => set({ owner: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="gap-acceptance">{t("Client acceptance")}</Label>
          <select
            id="gap-acceptance"
            value={draft.acceptance}
            onChange={(e) => set({ acceptance: e.target.value as GapAcceptance })}
            className={SELECT_CLASS}
          >
            {Object.entries(ACCEPTANCE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {t(label)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t("Cancel")}
        </Button>
        <Button onClick={handleSave} disabled={save.isPending}>
          {t("Save GAP")}
        </Button>
      </DialogFooter>
    </>
  );
}
